import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { testimonials } from '@/mocks/manga';

function AboutSEO() {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://sakuranga.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://sakuranga.com/about' },
        ],
      },
      {
        '@type': 'AboutPage',
        name: '关于樱花动漫之家 — 日漫推荐与排行平台',
        description: '了解樱花动漫之家的品牌故事、使命愿景以及联系方式。我们致力于为日漫爱好者提供优质的日本漫画推荐与排行服务。',
        url: 'https://sakuranga.com/about',
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

function ContactForm() {
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(false);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormSuccess(true);
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    const textarea = formData.get('message') as string;
    if (textarea && textarea.length > 500) {
      setFormError('留言内容不能超过500个字符');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9opa7qftmtnfcfh0bl0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      if (response.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        const serverMsg =
          (parsed as { meta?: { message?: string } })?.meta?.message ||
          (parsed as { message?: string })?.message ||
          (parsed as { meta?: { detail?: string } })?.meta?.detail ||
          responseText;
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
            姓名 <span className="text-primary-500">*</span>
          </label>
          <input
            type="text"
            id="contact-name"
            name="name"
            required
            className="w-full h-10 px-3.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
            placeholder="请输入您的姓名"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            type="email"
            id="contact-email"
            name="email"
            required
            className="w-full h-10 px-3.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
            placeholder="请输入您的邮箱"
          />
        </div>
      </div>
      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
          主题
        </label>
        <input
          type="text"
          id="contact-subject"
          name="subject"
          className="w-full h-10 px-3.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          placeholder="请选择或输入主题"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={4}
          maxLength={500}
          className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
          placeholder="请输入您的留言（500字以内）"
        />
        <p className="text-xs text-foreground-400 mt-1">最多输入500个字符</p>
      </div>

      <input
        type="text"
        name="phone_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-honeypot"
      />

      {formError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">
          {formError}
        </div>
      )}

      {formSuccess && (
        <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5">
          感谢您的留言！我们会尽快回复您。
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
      >
        {submitting ? (
          <>
            <div className="w-4 h-4 flex items-center justify-center animate-spin">
              <i className="ri-loader-4-line text-sm"></i>
            </div>
            提交中...
          </>
        ) : (
          <>
            发送留言
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-send-plane-line text-sm"></i>
            </div>
          </>
        )}
      </button>
    </form>
  );
}

export default function AboutPage() {
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <Layout>
      <AboutSEO />
      <div ref={topRef} className="pt-20 pb-14 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-6">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
            <span className="text-foreground-300">/</span>
            <span className="text-foreground-700">关于我们</span>
          </nav>

          {/* Brand Introduction */}
          <section className="mb-14 md:mb-20">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              关于樱花动漫之家
            </h1>
            <p className="text-sm text-foreground-500 mb-8">
              为日漫爱好者而生，连接你与最精彩的日本漫画世界
            </p>

            <div className="space-y-6 text-sm text-foreground-600 leading-relaxed">
              <p>
                樱花动漫之家成立于2020年，是一个专注于<strong>日本漫画（日漫）</strong>资讯与资源聚合的平台。我们致力于为全球华语日漫爱好者提供最全面、最高质量的<strong>日漫推荐</strong>与<strong>日漫排行</strong>服务，帮助每一位日漫爱好者轻松发现适合自己的优秀漫画作品。
              </p>
              <p>
                我们的编辑团队由一群资深的日漫爱好者组成，每天追踪最新的<strong>连载日漫</strong>动态，精选热门与冷门佳作。从<strong>经典日漫</strong>名作到最新的连载作品，从热血少年漫到治愈日常番，我们的日漫资源涵盖了几乎所有类型。
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="text-2xl font-heading font-bold text-primary-500 mb-1">2020<span className="text-sm font-normal">年</span></div>
                  <p className="text-xs text-foreground-500">正式成立上线</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="text-2xl font-heading font-bold text-primary-500 mb-1">1000+</div>
                  <p className="text-xs text-foreground-500">日漫资源收录</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="text-2xl font-heading font-bold text-primary-500 mb-1">100万+</div>
                  <p className="text-xs text-foreground-500">累计服务用户</p>
                </div>
              </div>
            </div>
          </section>

          {/* Mission & Vision */}
          <section className="mb-14 md:mb-20">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              使命与愿景
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-focus-3-line text-lg"></i>
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  让每一位日漫爱好者都能轻松找到心仪的漫画作品。我们通过精准的推荐算法和专业的编辑团队，为日漫爱好者提供个性化的漫画发现体验。
                </p>
              </div>
              <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-eye-line text-lg"></i>
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  成为华语地区最受欢迎的日漫推荐平台。我们希望通过优质的内容和极致的用户体验，打造一个让日漫爱好者流连忘返的漫画家园。
                </p>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-14 md:mb-20">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(t.rating)].map((_, j) => (
                      <div key={j} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{t.comment}&rdquo;</p>
                  <div className="flex items-center gap-2.5">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className="text-sm font-medium text-foreground-950">{t.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8">
              <div className="md:col-span-3">
                <ContactForm />
              </div>
              <div className="md:col-span-2 space-y-5">
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                  <p className="text-sm text-foreground-600">contact@sakuranga.com</p>
                  <p className="text-xs text-foreground-500 mt-0.5">工作日24小时内回复</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                    <i className="ri-customer-service-2-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-1">用户反馈</h3>
                  <p className="text-sm text-foreground-600">support@sakuranga.com</p>
                  <p className="text-xs text-foreground-500 mt-0.5">意见与建议随时欢迎</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                    <i className="ri-links-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-1">社交媒体</h3>
                  <div className="flex items-center gap-3 mt-2">
                    <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer" aria-label="微博">
                      <i className="ri-weibo-line"></i>
                    </a>
                    <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer" aria-label="微信">
                      <i className="ri-wechat-line"></i>
                    </a>
                    <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer" aria-label="B站">
                      <i className="ri-bilibili-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}